import * as React from 'react';
import { Component } from 'react';
import Lazy from 'lazy.js';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { History, Location } from 'history';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Button } from 'Src/components/Button/Button';
import { ClubWizardSummaryStep } from './ClubWizardSteps/ClubWizardSummaryStep';
import { Sport } from 'Src/models/sport';
import { Loader } from 'Src/components/Loader/Loader';
import {
  searchFunctionClubSports,
  searchFunctionCoaches,
  searchFunctionMembersOfStaff,
  searchFunctionVenues
} from 'Src/helpers/autocomplete/autocomplete';
import { GENDER, SPORT_GENDER_CLIENT_TO_SERVER_MAPPING } from 'Src/consts/common';
import { USER_STATUS } from 'Src/consts/user';
import { SchoolForm } from 'Src/models/form';
import { DEFAULT_LIMIT, DEFAULT_SKIP, FIRST_PAGE } from 'Src/consts/table';
import { getSchoolForms } from 'Src/helpers/service/admin/user';
import { MultiSelectCheckboxItem } from 'Src/components/MultiSelectCheckboxes/MultiSelectCheckboxes';
import { parse } from 'query-string';
import {
  getAgesForMultiSelect,
  getDaysForMultiselect,
  getDefaultGendersForMultiSelect,
  getGendersForMultiSelect,
  getTermsForMultiSelect
} from 'Src/helpers/multiselect/multiselect';
import { getSchoolTerms } from 'Src/helpers/service/admin/schoolTerms';
import { ClubWizardScheduleStep } from './ClubWizardSteps/ClubWizardScheduleStep';
import { ClubWizardBookingStep } from './ClubWizardSteps/ClubWizardBookingStep';
import { ClubPaymentSettingsStep } from './ClubWizardSteps/ClubPaymentSettingsStep';
import { CLUB_SCHEDULE, CLUB_STAFF, CLUB_PRICING } from 'Src/consts/club';
import { SCHOOL_CLUB_INVITATIONS_TYPE } from 'Src/types/school';
import { ClubWizardVenueStep } from './ClubWizardSteps/ClubWizardVenueStep';
import { SchoolTerm } from 'Src/models/schoolTerm';
import { WEEK_DAYS } from 'Src/consts/date';
import {
  createClub,
  getClub,
  updateClub,
  previewActiveClubUpdate,
  applyActiveClubUpdate,
  ActiveClubUpdatePlan
} from 'Src/helpers/service/admin/clubs';
import { VenueForm } from 'Src/views/GenericView/AdminView/School/Venues/VenueForm/VenueForm';
import { createVenue } from 'Src/helpers/service/admin/venues';
import { Club, ClubSport, ClubStaff, ClubPaymentSettings, ClubPrice } from 'Src/models/club';
import {
  getClubCoachesForForm,
  getClubMembersOfStaffForForm,
  getClubTime,
  getStaffPermissionId
} from 'Src/helpers/club/club';
import { MODE_TYPE } from '../../../../../../consts/common';
import { getAllClubParticipants } from '../../../../../../helpers/service/admin/clubParticipants';
import { ClubParticipantsExtended } from '../../../../../../models/club';
import { addClubStudents } from '../../../../../../helpers/service/admin/clubStudents';
import { USER_GENDER } from '../../../../../../consts/user';
import { DEFAULT_CLUB_TABLE_FILTERS } from '../Clubs';
import { AGE_GROUPS_SORTED } from 'Src/consts/school';
import { getAcademicYearString, getAgesGroupFormatted } from 'Src/helpers/accessor/accessor';
import { getPaymentAccount, getAllPaymentsAccounts } from 'Src/helpers/service/admin/paymentAccounts';
import { isUserTeacher, isUserCoach } from 'Src/helpers/user/user';
import './ClubWizard.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

type Step = 'SUMMARY' | 'SCHEDULE' | 'LOCATION' | 'BOOKING' | 'PAYMENT_SETTINGS';

const CLUB_WIZARD_STEP: {
  [key: string]: Step;
} = {
  SUMMARY: 'SUMMARY',
  SCHEDULE: 'SCHEDULE',
  LOCATION: 'LOCATION',
  BOOKING: 'BOOKING',
  PAYMENT_SETTINGS: 'PAYMENT_SETTINGS'
};

const CLUB_WIZARD_STEP_MAPPING: {
  [key: string]: string;
} = {
  SUMMARY: 'Summary',
  SCHEDULE: 'Schedule',
  LOCATION: 'Location',
  BOOKING: 'Booking',
  PAYMENT_SETTINGS: 'Payment settings'
};

const CLUB_WIZARD_STEP_HINTS: { [key: string]: string } = {
  SUMMARY: 'Club details, audience and staffing',
  SCHEDULE: 'Dates, terms and session times',
  LOCATION: 'Venue and meeting point',
  BOOKING: 'Capacity and booking forms',
  PAYMENT_SETTINGS: 'Price and payment account'
};

const CLUB_WIZARD_STEP_EXPLANATIONS: { [key: string]: string } = {
  SUMMARY: 'Change the name, activity and who the club is for.',
  SCHEDULE: 'Choose either a date range or the school terms, then set when sessions run.',
  LOCATION: 'Select an existing venue or create one, then add a meeting-point note.',
  BOOKING: 'Set the capacity and decide who booking forms are addressed to.',
  PAYMENT_SETTINGS: 'Configure the price and the account used to collect payments.'
};

interface State {
  currentStep: Step;
  maxReachedStepIndex: number;
  isCancelModalOpen: boolean;
  isLoading: boolean;
  isDirty: boolean;
  clubStatus: string;
  scheduleMode: 'DATES' | 'TERMS' | 'DATES_LIST';
  specificDates: Date[];

  //club fields
  name: string;
  description: string;
  time: string;
  location: string;
  repeatEveryWeeks: number;
  maxParticipants: number;
  duration: number;
  sport: Sport | ClubSport;
  forms: SchoolForm[];
  ageItems: MultiSelectCheckboxItem[];
  termItems: MultiSelectCheckboxItem[];
  dayItems: MultiSelectCheckboxItem[];
  genderItems: MultiSelectCheckboxItem[];
  start: Date;
  end: Date;
  invitations: SCHOOL_CLUB_INVITATIONS_TYPE;
  //autoenrollment: CLUB_AUTOENROLLMENT_TYPE;
  venue: any;
  schoolTerms: SchoolTerm[];
  coaches: ClubStaff[];
  membersOfStaff: ClubStaff[];
  clubParticipants: ClubParticipantsExtended[];
  currentClubAges: number[];
  currentClubGender: string;
  price: ClubPrice;
  paymentAccount: any;
  isPaymentRequired: boolean;
  //errors
  errors: ClubWizardErrors;

  isCreateVenueFormOpen: boolean;
  isSaveOnlyPassingStudentsModalOpen: boolean;
  activeUpdatePlan?: ActiveClubUpdatePlan;
  pendingActiveUpdate?: any;
  includePastOccurrences: boolean;
  selectedActiveUpdateOccurrences: string[];
}

export interface ClubWizardErrors {
  name: boolean;
  sport: boolean;
  gender: boolean;
  duration: boolean;
  days: boolean;
  interval: boolean;
  venue: boolean;
  date: boolean;
  price: boolean;
  paymentAccount: boolean;
  isPaymentRequired: boolean;
}

export class ClubWizard extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const now = new Date();

    const { user } = props;
    const { activeSchool } = user;
    const { clubInvitations, defaultClubStartTime } = activeSchool;
    const isDefaultClubStartTimeExist = typeof defaultClubStartTime !== 'undefined';

    this.state = {
      currentStep: CLUB_WIZARD_STEP.SUMMARY,
      maxReachedStepIndex: 0,
      isCancelModalOpen: false,
      isLoading: false,
      isDirty: false,
      clubStatus: 'DRAFT',
      scheduleMode: 'DATES',
      specificDates: [],
      name: '',
      description: '',
      sport: undefined,
      forms: [],
      ageItems: [],
      termItems: [],
      dayItems: [],
      genderItems: [],
      start: now,
      end: now,
      repeatEveryWeeks: 1,
      duration: 0,
      maxParticipants: 0,
      time: isDefaultClubStartTimeExist ? defaultClubStartTime : '10:00',
      invitations: clubInvitations,
      //autoenrollment: CLUB_AUTOENROLLMENT.ON,
      location: '',
      venue: undefined,
      schoolTerms: [],
      errors: {
        name: false,
        sport: false,
        gender: false,
        duration: false,
        days: false,
        interval: false,
        venue: false,
        date: false,
        paymentAccount: false,
        isPaymentRequired: false,
        price: false
      },
      isCreateVenueFormOpen: false,
      isSaveOnlyPassingStudentsModalOpen: false,
      activeUpdatePlan: undefined,
      pendingActiveUpdate: undefined,
      includePastOccurrences: false,
      selectedActiveUpdateOccurrences: [],
      membersOfStaff: [],
      clubParticipants: [],
      currentClubAges: [],
      currentClubGender: '',
      coaches: [],
      price: undefined,
      paymentAccount: '',
      isPaymentRequired: false
    };
  }

  componentDidMount() {
    this.updateTopbarWizardHeader();
    const { user } = this.props;
    const { activeSchool } = user;
    const { isPaymentsEnabled } = activeSchool;

    const filter = {
      limit: DEFAULT_LIMIT,
      skip: DEFAULT_SKIP
    };

    this.setState({
      isLoading: true
    });

    const clubId = this.getClubId();
    const clubMode = this.getClubMode();
    const isCopyClubWithParticipantsMode = clubMode === MODE_TYPE.CLUB_COPY_WITH_PARTICIPANTS;

    const schoolFormsPromise = getSchoolForms(user, filter);
    const schoolTermsPromise = getSchoolTerms(user);
    const isClubExist = typeof clubId !== 'undefined';
    const clubPromise = isClubExist ? getClub(user, clubId) : BPromise.resolve();
    const clubParticipantsPromise = isCopyClubWithParticipantsMode
      ? getAllClubParticipants(user, clubId)
      : BPromise.resolve([]);

    let isPaymentAccountExist;
    let isPaymentRequired;

    BPromise.all([schoolFormsPromise, schoolTermsPromise, clubPromise, clubParticipantsPromise])
      .then(([forms, schoolTerms, club, clubParticipants]) => {
        if (typeof club !== 'undefined') {
          const {
            name,
            description,
            schedule,
            sport,
            gender,
            ages,
            venue,
            maxParticipants,
            invitations,
            location: clubLocation,
            paymentSettings,
            price
          } = club as Club;

          const { startDate, finishDate, duration, scheduleType, intervals, days, dates, repeatEveryWeeks } = schedule;
          isPaymentRequired = propz.get(paymentSettings, ['askToPay'], false);

          const ageItems = getAgesForMultiSelect(activeSchool, forms, ages);
          const termItems =
            scheduleType === CLUB_SCHEDULE.INTERVALS
              ? getTermsForMultiSelect(schoolTerms, intervals)
              : getTermsForMultiSelect(schoolTerms);

          const dayItems = getDaysForMultiselect(days);
          const { terms } = schoolTerms;

          const genderItems = getGendersForMultiSelect(sport, gender);
          const now = new Date();

          const coaches = getClubCoachesForForm(club);
          const membersOfStaff = getClubMembersOfStaffForForm(club);

          const activeClubParticipants = clubParticipants.filter(
            participant => participant.permissionStatus === USER_STATUS.ACTIVE
          );

          this.setState({
            schoolTerms: terms,
            forms,
            ageItems,
            termItems,
            dayItems,
            name,
            description,
            sport,
            genderItems,
            duration,
            venue,
            time: getClubTime(club),
            maxParticipants,
            invitations,
            repeatEveryWeeks,
            location: typeof clubLocation !== 'undefined' ? clubLocation : '',
            start:
              scheduleType === CLUB_SCHEDULE.WEEKLY_SCHEDULE
                ? new Date(startDate)
                : scheduleType === CLUB_SCHEDULE.DATES_LIST && dates && dates.length
                ? new Date(dates[0])
                : now,
            end:
              scheduleType === CLUB_SCHEDULE.WEEKLY_SCHEDULE
                ? new Date(finishDate)
                : scheduleType === CLUB_SCHEDULE.DATES_LIST && dates && dates.length
                ? new Date(dates[dates.length - 1])
                : now,
            coaches,
            membersOfStaff,
            clubParticipants: activeClubParticipants,
            currentClubAges: ages,
            currentClubGender: gender,
            clubStatus: club.status || 'DRAFT',
            scheduleMode:
              scheduleType === CLUB_SCHEDULE.INTERVALS
                ? 'TERMS'
                : scheduleType === CLUB_SCHEDULE.DATES_LIST
                ? 'DATES_LIST'
                : 'DATES',
            specificDates: Array.isArray(dates) ? dates.map(date => new Date(date)) : [],
            price,
            isPaymentRequired
          });
        } else {
          const ageItems = getAgesForMultiSelect(activeSchool, forms);
          const genderItems = getDefaultGendersForMultiSelect();
          const termItems = getTermsForMultiSelect(schoolTerms);
          const dayItems = getDaysForMultiselect();
          const { terms } = schoolTerms;

          this.setState({
            forms,
            ageItems,
            genderItems,
            termItems,
            dayItems,
            schoolTerms: terms,
            price: { price: 0, currency: 'POUND', priceType: 'TERM' }
          });
        }

        const paymentAccountId = propz.get(club, ['paymentSettings', 'paymentAccountId']);
        isPaymentAccountExist = typeof paymentAccountId !== 'undefined';
        let paymentAccountPromise;

        switch (true) {
          case isClubExist && isPaymentsEnabled:
            paymentAccountPromise = isPaymentAccountExist
              ? getPaymentAccount(user, paymentAccountId)
              : getAllPaymentsAccounts(user);
            break;
          case !isClubExist && isPaymentsEnabled:
            paymentAccountPromise = getAllPaymentsAccounts(user);
            break;
          default:
            paymentAccountPromise = BPromise.resolve();
            break;
        }

        return paymentAccountPromise;
      })
      .then(paymentAccountOrAccounts => {
        switch (true) {
          case isPaymentAccountExist:
            this.setState({
              paymentAccount: paymentAccountOrAccounts,
              isLoading: false
            });

            break;
          case isPaymentsEnabled:
            const paymentAccountDefault = paymentAccountOrAccounts.find(account => account.isDefault);

            this.setState({
              paymentAccount: paymentAccountDefault,
              isLoading: false
            });

            break;
          default:
            this.setState({
              isLoading: false
            });
            break;
        }
      });
  }

  componentDidUpdate() {
    this.updateTopbarWizardHeader();
  }

  componentWillUnmount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-wizard-clear'));
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  updateTopbarWizardHeader() {
    const steps = this.getSteps();
    const currentStepIndex = steps.findIndex(step => step === this.state.currentStep);
    const maxReachedStepIndex =
      this.getClubMode() === MODE_TYPE.EDIT ? steps.length - 1 : this.state.maxReachedStepIndex;
    const clubMode = this.getClubMode();
    const title =
      clubMode === MODE_TYPE.EDIT
        ? 'Edit club'
        : clubMode === MODE_TYPE.CLUB_COPY || clubMode === MODE_TYPE.CLUB_COPY_WITH_PARTICIPANTS
        ? 'Copy club'
        : 'New club';

    window.dispatchEvent(
      new CustomEvent('sit:topbar-wizard', {
        detail: {
          steps: steps.map((step, index) => ({
            label: CLUB_WIZARD_STEP_MAPPING[step],
            state: index === currentStepIndex ? 'active' : index <= maxReachedStepIndex ? 'done' : 'pending'
          }))
        }
      })
    );
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: `Clubs / ${title}` }));
  }

  getClubId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    const clubId = search.club;
    return clubId;
  }

  getClubMode(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    return search.mode;
  }

  onInputChange = event => {
    const { errors } = this.state;
    event.preventDefault();
    const { target } = event;
    const { value, name } = target;

    const isTargetNameRepeatEveryWeeks = name === 'repeatEveryWeeks';
    const isTargetNameDuration = name === 'duration';
    const isTargetNameMaxParticipants = name === 'maxParticipants';

    const isConvertToNumberRequired =
      isTargetNameRepeatEveryWeeks || isTargetNameDuration || isTargetNameMaxParticipants;

    const valueConverted = isConvertToNumberRequired ? Number(value) : value;

    const isTargetNameEqualName = name == 'name';
    const isTargetNameEqualDuration = name == 'duration';
    const isResetErrorRequired = isTargetNameEqualName || isTargetNameEqualDuration;

    if (isResetErrorRequired) {
      this.setState(({
        [name]: valueConverted,
        isDirty: true,
        errors: {
          ...errors,
          [name]: false
        }
      } as unknown) as State);
    } else {
      this.setState(({ [name]: valueConverted, isDirty: true } as unknown) as State);
    }
  };

  onInvitationsChange = (invitations: SCHOOL_CLUB_INVITATIONS_TYPE): void => {
    this.setState({ invitations, isDirty: true });
  };

  getSteps(): Step[] {
    const { user } = this.props;
    const { activeSchool } = user;
    if (activeSchool.isPaymentsEnabled) {
      return [
        CLUB_WIZARD_STEP.SUMMARY,
        CLUB_WIZARD_STEP.SCHEDULE,
        CLUB_WIZARD_STEP.LOCATION,
        CLUB_WIZARD_STEP.BOOKING,
        CLUB_WIZARD_STEP.PAYMENT_SETTINGS
      ];
    } else {
      return [CLUB_WIZARD_STEP.SUMMARY, CLUB_WIZARD_STEP.SCHEDULE, CLUB_WIZARD_STEP.LOCATION, CLUB_WIZARD_STEP.BOOKING];
    }
  }

  getStepTitle(): string {
    const { currentStep } = this.state;
    return CLUB_WIZARD_STEP_MAPPING[currentStep];
  }

  onNextClick = () => {
    const {
      currentStep,
      name,
      sport,
      errors,
      genderItems,
      dayItems,
      duration,
      termItems,
      start,
      end,
      venue,
      currentClubGender,
      currentClubAges,
      ageItems
    } = this.state;
    const steps = this.getSteps();
    const currentStepIndex = steps.findIndex(step => step === currentStep);
    const totalSteps = steps.length;

    if (currentStep === CLUB_WIZARD_STEP.SUMMARY) {
      const isNameExist = name !== '';
      const isSportExist = typeof sport !== 'undefined';
      const isGenderExist = genderItems.some(genderItem => genderItem.isChecked);
      const dataToPost = this.getDataToPost();
      const isChangeAge = [...currentClubAges].sort().toString() !== [...dataToPost.ages].sort().toString();
      const isChangeGender = dataToPost.gender !== currentClubGender;
      const clubMode = this.getClubMode();
      const isCopyClubWithParticipantsMode = clubMode === MODE_TYPE.CLUB_COPY_WITH_PARTICIPANTS;
      const isAgesNotChanged = ageItems.every(item => item.isChecked === false);

      const isError = !isNameExist || !isSportExist || !isGenderExist;

      if (isError) {
        this.setState({
          errors: {
            ...errors,
            name: !isNameExist,
            sport: !isSportExist,
            gender: !isGenderExist
          }
        });
        return;
      }

      if (isAgesNotChanged) {
        const ageItemsUpdated = ageItems.map(item => {
          return {
            ...item,
            isChecked: true
          };
        });

        this.setState({
          ageItems: ageItemsUpdated
        });
      }

      if (isCopyClubWithParticipantsMode && (isChangeAge || isChangeGender)) {
        this.setState({
          isSaveOnlyPassingStudentsModalOpen: true
        });
        return;
      }
    }

    if (currentStep === CLUB_WIZARD_STEP.SCHEDULE) {
      const isIntervalExist =
        this.state.scheduleMode === 'TERMS'
          ? termItems.some(termItem => termItem.isChecked)
          : this.state.scheduleMode === 'DATES_LIST'
          ? this.state.specificDates.length > 0
          : start !== end;
      const isDurationExist = !isNaN(duration) && duration !== 0;
      const isDaysExist = this.state.scheduleMode === 'DATES_LIST' || dayItems.some(dayItem => dayItem.isChecked);

      const isError = !isIntervalExist || !isDurationExist || !isDaysExist;

      if (isError) {
        this.setState({
          errors: {
            ...errors,
            interval: !isIntervalExist,
            days: !isDaysExist,
            duration: !isDurationExist
          }
        });
        return;
      }
    }

    if (currentStep === CLUB_WIZARD_STEP.LOCATION) {
      const isVenueExist = typeof venue !== 'undefined';
      const isError = !isVenueExist;

      if (isError) {
        this.setState({
          errors: {
            ...errors,
            venue: !isVenueExist
          }
        });
        return;
      }
    }

    if (currentStepIndex < totalSteps - 1) {
      const nextStepIndex = currentStepIndex + 1;
      this.setState({
        currentStep: steps[nextStepIndex],
        maxReachedStepIndex: Math.max(this.state.maxReachedStepIndex, nextStepIndex)
      });
    }
  };

  onPreviousClick = () => {
    const { currentStep } = this.state;
    const steps = this.getSteps();
    const currentStepIndex = steps.findIndex(step => step === currentStep);

    if (currentStepIndex > 0) {
      this.setState({
        currentStep: steps[currentStepIndex - 1]
      });
    }
  };

  onCancelClick = () => {
    const { isDirty } = this.state;
    const { history } = this.props;
    const search = propz.get(history, ['location', 'state', 'search'], '');

    if (!isDirty) {
      const clubId = this.getClubId();
      history.push({
        pathname: '/clubs',
        search,
        state: { clubId: clubId }
      });
    } else {
      this.setState({
        isCancelModalOpen: true
      });
    }
  };

  renderCancelModal() {
    const { isCancelModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isCancelModalOpen}
        title="Unsaved changes"
        showFooter={false}
        isBodyBold={false}
        customClass="clubWizardModal clubWizardCancelModal"
        dialogStyle={{ maxWidth: 480 }}
        onCloseClick={this.onCancelModalCloseIconClick}
        onCloseIconClick={this.onCancelModalCloseIconClick}
        onBackdropClick={this.onCancelModalCloseIconClick}
      >
        <div className="clubWizardCancelModal__content">
          <div className="clubWizardCancelModal__notice">
            <span aria-hidden="true">!</span>
            <div>
              <strong>Save this club as a draft?</strong>
              <p>You have unsaved changes. Save a draft to continue later, or leave without saving them.</p>
            </div>
          </div>
          <div className="clubWizardCancelModal__actions">
            <button type="button" onClick={this.onCancelModalCloseIconClick}>
              Keep editing
            </button>
            <button type="button" className="isDiscard" onClick={this.onCancelModalClick}>
              Leave without saving
            </button>
            <button type="button" className="isPrimary" onClick={this.onSaveClick}>
              Save as draft
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  onCancelModalCloseIconClick = () => {
    this.setState({
      isCancelModalOpen: false
    });
  };

  onCancelModalClick = () => {
    const { history } = this.props;
    const search = propz.get(history, ['location', 'state', 'search'], '');
    const clubId = this.getClubId();

    history.push({
      pathname: '/clubs',
      search,
      state: { clubId: clubId }
    });
  };

  getSports = (text: string) => {
    const { user } = this.props;

    const isUserTeacherOrCoach = isUserTeacher(user) || isUserCoach(user);

    const userPermissionSports = propz.get(user, ['activePermission', 'sports'], []);
    const userPermissionSportsIds = userPermissionSports.map(sport => sport.id);
    const isUserPermissionSportsExist = userPermissionSports.length > 0;

    const where = {};

    if (isUserTeacherOrCoach && isUserPermissionSportsExist) {
      propz.set(where, ['id'], {
        $in: userPermissionSportsIds
      });
    }

    return searchFunctionClubSports(user, text, where);
  };

  getVenues = (text: string) => {
    const { user } = this.props;
    return searchFunctionVenues(user, text);
  };

  onSportSelect = (sport: Sport) => {
    const { errors } = this.state;

    const genderItems = getGendersForMultiSelect(sport);

    this.setState({
      sport,
      genderItems,
      isDirty: true,
      errors: {
        ...errors,
        sport: false
      }
    });
  };

  onVenueSelect = (venue: any) => {
    const { errors } = this.state;
    this.setState({
      venue,
      isDirty: true,
      errors: {
        ...errors,
        venue: false
      }
    });
  };

  onPaymentAccountChange = (paymentAccount: any) => {
    const { errors, isPaymentRequired } = this.state;
    const isPaymentAccountExist = typeof paymentAccount !== 'undefined';

    this.setState({
      paymentAccount,
      isDirty: true,
      errors: {
        ...errors,
        paymentAccount: isPaymentRequired && !isPaymentAccountExist
      }
    });
  };

  onIsPaymentRequiredChange = (value: boolean) => {
    const { errors } = this.state;

    if (!value) {
      this.setState({
        isPaymentRequired: value,
        isDirty: true,
        errors: {
          ...errors,
          price: false,
          paymentAccount: false
        }
      });
    } else {
      this.setState({
        isPaymentRequired: value,
        isDirty: true
      });
    }
  };

  onPriceChange = (newPrice: string) => {
    const { errors, price, isPaymentRequired } = this.state;

    const newPriceNumber = Number(newPrice);
    const isNumber = Number.isFinite(newPriceNumber);
    const isErrorExist = isPaymentRequired && newPriceNumber === 0;

    this.setState({
      price: isNumber ? { ...price, price: newPriceNumber } : price,
      isDirty: true,
      errors: {
        ...errors,
        price: isErrorExist
      }
    });
  };

  onGenderChange = (index: number) => {
    const { genderItems, errors } = this.state;
    const genderItemsUpdated = genderItems.map((genderItem, genderIndex) => {
      return genderIndex === index
        ? {
            ...genderItem,
            isChecked: !genderItem.isChecked
          }
        : genderItem;
    });

    const isGenderExist = genderItemsUpdated.some(genderItem => genderItem.isChecked);

    this.setState({
      genderItems: genderItemsUpdated,
      isDirty: true,
      errors: {
        ...errors,
        gender: !isGenderExist
      }
    });
  };

  onAgesChange = (index: number) => {
    const { ageItems } = this.state;
    const ageItemsUpdated = ageItems.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    this.setState({
      ageItems: ageItemsUpdated,
      isDirty: true
    });
  };

  onAllAgesChange = () => {
    const shouldSelectAll = !this.state.ageItems.some(item => item.isChecked);
    this.setState({
      ageItems: this.state.ageItems.map(item => ({ ...item, isChecked: shouldSelectAll })),
      isDirty: true
    });
  };

  onScheduleModeChange = (scheduleMode: 'DATES' | 'TERMS' | 'DATES_LIST') => {
    this.setState({
      scheduleMode,
      termItems:
        scheduleMode === 'DATES'
          ? this.state.termItems.map(item => ({ ...item, isChecked: false }))
          : this.state.termItems,
      isDirty: true
    });
  };

  onSpecificDatesChange = (specificDates: Date[]) => {
    this.setState({ specificDates, isDirty: true });
  };

  onTermsChange = (index: number) => {
    const { termItems, errors } = this.state;
    const termItemsUpdated = termItems.map((term, termIndex) => {
      return termIndex === index
        ? {
            ...term,
            isChecked: !term.isChecked
          }
        : term;
    });

    this.setState({
      termItems: termItemsUpdated,
      isDirty: true,
      errors: {
        ...errors,
        interval: false
      }
    });
  };

  onDaysChange = (index: number) => {
    const { dayItems, errors } = this.state;
    const dayItemsUpdated = dayItems.map((day, dayIndex) => {
      return dayIndex === index
        ? {
            ...day,
            isChecked: !day.isChecked
          }
        : day;
    });

    this.setState({
      dayItems: dayItemsUpdated,
      isDirty: true,
      errors: {
        ...errors,
        days: false
      }
    });
  };

  onStartDateChange = (date: Date) => {
    const { errors, end } = this.state;
    const isStartBiggerThenEnd = Number(date) > Number(end);
    if (date) {
      this.setState({
        start: date,
        isDirty: true,
        errors: {
          ...errors,
          interval: false,
          date: isStartBiggerThenEnd
        }
      });
    }
  };

  onEndDateChange = (date: Date) => {
    const { errors, start } = this.state;
    const isEndLessThenStart = Number(date) < Number(start);
    if (date) {
      this.setState({
        end: date,
        isDirty: true,
        errors: {
          ...errors,
          interval: false,
          date: isEndLessThenStart
        }
      });
    }
  };

  createVenueFormOpen = (): void => {
    this.setState({
      isCreateVenueFormOpen: true
    });
  };

  setCoaches = (coaches: ClubStaff[]): void => {
    this.setState({
      coaches,
      isDirty: true
    });
  };

  setMembersOfStaff = (membersOfStaff: ClubStaff[]): void => {
    this.setState({
      membersOfStaff,
      isDirty: true
    });
  };

  getCoachesSearchFunction = (text: string) => {
    const { user } = this.props;
    const { coaches, sport } = this.state;
    const activeSchoolId = user.activeSchoolId;
    return searchFunctionCoaches(user, text, activeSchoolId, coaches, sport.id);
  };

  getMembersOfStaffSearchFunction = (text: string) => {
    const { user } = this.props;
    const { membersOfStaff, sport } = this.state;
    const activeSchoolId = user.activeSchoolId;
    return searchFunctionMembersOfStaff(user, text, activeSchoolId, membersOfStaff, sport.id);
  };

  renderViewByStep(): React.ReactNode {
    const { user } = this.props;
    const {
      currentStep,
      name,
      description,
      sport,
      ageItems,
      termItems,
      start,
      end,
      dayItems,
      repeatEveryWeeks,
      duration,
      maxParticipants,
      invitations,
      //autoenrollment,
      location,
      venue,
      time,
      errors,
      genderItems,
      coaches,
      membersOfStaff,
      price,
      paymentAccount,
      isPaymentRequired
    } = this.state;
    switch (currentStep) {
      case CLUB_WIZARD_STEP.SUMMARY:
        return (
          <ClubWizardSummaryStep
            onInputChange={this.onInputChange}
            name={name}
            description={description}
            sport={sport}
            onSportSelect={this.onSportSelect}
            getSports={this.getSports}
            ageItems={ageItems}
            genderItems={genderItems}
            onAgesChange={this.onAgesChange}
            onAllAgesChange={this.onAllAgesChange}
            onGenderChange={this.onGenderChange}
            errors={errors}
            coaches={coaches}
            membersOfStaff={membersOfStaff}
            setCoaches={this.setCoaches}
            setMembersOfStaff={this.setMembersOfStaff}
            getCoachesSearchFunction={this.getCoachesSearchFunction}
            getMembersOfStaffSearchFunction={this.getMembersOfStaffSearchFunction}
            isActive={this.state.clubStatus === 'ACTIVE'}
          />
        );

      case CLUB_WIZARD_STEP.SCHEDULE:
        return (
          <ClubWizardScheduleStep
            onStartDateChange={this.onStartDateChange}
            onEndDateChange={this.onEndDateChange}
            start={start}
            end={end}
            duration={duration}
            time={time}
            termItems={termItems}
            onTermsChange={this.onTermsChange}
            onDaysChange={this.onDaysChange}
            dayItems={dayItems}
            repeatEveryWeeks={repeatEveryWeeks}
            onInputChange={this.onInputChange}
            errors={errors}
            name={name}
            sport={sport}
            genderItems={genderItems}
            ageItems={ageItems}
            scheduleMode={this.state.scheduleMode}
            onScheduleModeChange={this.onScheduleModeChange}
            specificDates={this.state.specificDates}
            onSpecificDatesChange={this.onSpecificDatesChange}
          />
        );

      case CLUB_WIZARD_STEP.LOCATION:
        return (
          <ClubWizardVenueStep
            onVenueSelect={this.onVenueSelect}
            venue={venue}
            getVenues={this.getVenues}
            onInputChange={this.onInputChange}
            location={location}
            createVenueFormOpen={this.createVenueFormOpen}
            errors={errors}
            name={name}
            sport={sport}
            genderItems={genderItems}
            ageItems={ageItems}
          />
        );

      case CLUB_WIZARD_STEP.BOOKING:
        return (
          <ClubWizardBookingStep
            user={user}
            invitations={invitations}
            onInvitationsChange={this.onInvitationsChange}
            //autoenrollment={autoenrollment}
            onInputChange={this.onInputChange}
            maxParticipants={maxParticipants}
            name={name}
            sport={sport}
            genderItems={genderItems}
            ageItems={ageItems}
            start={start}
            end={end}
            dayItems={dayItems}
            time={time}
            duration={duration}
            venue={venue}
            scheduleMode={this.state.scheduleMode}
            termItems={termItems}
            specificDates={this.state.specificDates}
            isActive={this.state.clubStatus === 'ACTIVE'}
          />
        );

      case CLUB_WIZARD_STEP.PAYMENT_SETTINGS:
        return (
          <ClubPaymentSettingsStep
            user={user}
            price={price}
            paymentAccount={paymentAccount}
            isPaymentRequired={isPaymentRequired}
            errors={errors}
            onPriceChange={this.onPriceChange}
            onPaymentAccountChange={this.onPaymentAccountChange}
            onIsPaymentRequiredChange={this.onIsPaymentRequiredChange}
          />
        );
    }
  }

  getDataToPost() {
    //copy-paste from ClubForm.tsx
    const {
      start,
      end,
      name,
      location,
      description,
      sport,
      maxParticipants,
      termItems,
      invitations,
      venue,
      repeatEveryWeeks,
      schoolTerms,
      duration,
      time,
      dayItems,
      forms,
      ageItems,
      genderItems,
      coaches,
      membersOfStaff,
      price,
      isPaymentRequired,
      paymentAccount,
      scheduleMode,
      specificDates
    } = this.state;

    const { user } = this.props;
    const { activeSchool } = user;

    const coachesData = coaches.map(coach => {
      return {
        userId: coach.id,
        permissionId: getStaffPermissionId(coach),
        staffRole: CLUB_STAFF.COACH
      };
    });

    const membersOfStaffData = membersOfStaff.map(memberOfStaff => {
      return {
        userId: memberOfStaff.id,
        permissionId: getStaffPermissionId(memberOfStaff),
        staffRole: CLUB_STAFF.MEMBER_OF_STAFF
      };
    });

    const startDate = start;
    const finishDate = end;
    const startDateUTC = new Date(startDate);
    const finishDateUTC = new Date(finishDate);

    const days = Object.keys(WEEK_DAYS).filter((day, index) => dayItems[index].isChecked);

    const { ageGroupsNaming, isPaymentsEnabled } = activeSchool;
    const ageGroup = propz.get(AGE_GROUPS_SORTED, [ageGroupsNaming]);

    const formsUniq = Lazy(forms)
      .uniq('age')
      .sort((form1, form2) => {
        const ageIndex1 = ageGroup.indexOf(form1.ageGroup);
        const ageIndex2 = ageGroup.indexOf(form2.ageGroup);

        return ageIndex1 - ageIndex2;
      })
      .toArray();

    const ages: number[] = formsUniq.filter((form, index) => ageItems[index].isChecked).map(form => form.age);

    let gender;

    const genderItemsCount = genderItems.length;
    const genderItemsChecked = genderItems.filter(genderItem => genderItem.isChecked);
    const genderItemsCheckedCount = genderItemsChecked.length;

    switch (true) {
      //Boys or Girls
      case genderItemsCount === 1:
      case genderItemsCount === 2 && genderItemsCheckedCount === 1:
        gender = SPORT_GENDER_CLIENT_TO_SERVER_MAPPING[genderItemsChecked[0].name];
        break;
      case genderItemsCount === 2 && genderItemsCheckedCount === 2:
        gender = GENDER.MIXED;
        break;
      default:
        gender = GENDER.MIXED; // fallback
        console.error(`Gender not defined`);
    }

    const timeParts = time.split(':');
    const scheduleTimeBase = scheduleMode === 'DATES_LIST' && specificDates.length ? specificDates[0] : startDate;
    const scheduleTime = new Date(scheduleTimeBase);
    scheduleTime.setHours(Number(timeParts[0]), Number(timeParts[1]), 0, 0);
    const scheduleTimeIso = scheduleTime.toISOString();

    const schedule =
      scheduleMode === 'DATES_LIST'
        ? {
            scheduleType: CLUB_SCHEDULE.DATES_LIST,
            dates: specificDates.map(date => date.toISOString()),
            time: scheduleTimeIso,
            duration: duration
          }
        : scheduleMode === 'TERMS'
        ? {
            scheduleType: CLUB_SCHEDULE.INTERVALS,
            repeatEveryWeeks: repeatEveryWeeks,
            intervals: schoolTerms
              .filter((schoolTerm, index) => termItems[index].isChecked)
              .map(schoolTerm => {
                const startDate = new Date(schoolTerm.start);
                const endDate = new Date(schoolTerm.end);
                return {
                  start: startDate.toISOString(),
                  end: endDate.toISOString()
                };
              }),
            days: days,
            time: scheduleTimeIso,
            duration: duration
          }
        : {
            scheduleType: CLUB_SCHEDULE.WEEKLY_SCHEDULE,
            repeatEveryWeeks: repeatEveryWeeks,
            startDate: startDateUTC.toISOString(),
            finishDate: finishDateUTC.toISOString(),
            days: days,
            time: scheduleTimeIso,
            duration: duration
          };

    const dataToPost = {
      name: name,
      location: location,
      description: description,
      sportId: sport && sport.id, //temp solution
      gender: gender,
      ages: ages,
      maxParticipants: maxParticipants,
      invitations: invitations,
      venue: {
        placeId: venue && venue.id //temp solution
      },
      schedule: schedule,
      staff: [...coachesData, ...membersOfStaffData]
    };

    if (isPaymentsEnabled) {
      const paymentSettingsToPost = {
        askToPay: isPaymentRequired,
        requiredForBook: isPaymentRequired,
        paymentAccountId: paymentAccount ? paymentAccount.id : undefined
      };

      propz.set(dataToPost, ['paymentSettings'], paymentSettingsToPost);
      propz.set(dataToPost, ['price'], price);
    }

    return dataToPost;
  }

  getParticipantsFiltered = dataToPost => {
    const { clubParticipants } = this.state;

    const participantsFiltered = clubParticipants.filter(participant => {
      const participantAge = propz.get(participant, ['form', 'age']);
      const isExistAge = dataToPost.ages.length === 0 || dataToPost.ages.some(age => age === participantAge);
      let isEqualGender;
      if (dataToPost.gender === GENDER.MIXED) {
        isEqualGender = true;
      } else {
        isEqualGender =
          (dataToPost.gender === GENDER.MALE_ONLY && participant.gender === USER_GENDER.MALE) ||
          (dataToPost.gender === GENDER.FEMALE_ONLY && participant.gender === USER_GENDER.FEMALE);
      }
      return isExistAge && isEqualGender;
    });
    return participantsFiltered;
  };

  onSaveClick = () => {
    const { user, history } = this.props;
    const { currentStep, errors } = this.state;

    let isErrorsExist;

    if (currentStep === CLUB_WIZARD_STEP.PAYMENT_SETTINGS || !isErrorsExist) {
      const { price: priceObj, isPaymentRequired, paymentAccount } = this.state;

      const price = propz.get(priceObj, ['price'], 0);

      // if user doesn't change price and payment account and errors have not been cought
      const isPriceErrorExist = isPaymentRequired && price === 0;
      const isPaymentAccountErrorExist = isPaymentRequired && typeof paymentAccount === 'undefined';
      isErrorsExist = isPriceErrorExist || isPaymentAccountErrorExist;

      this.setState({
        errors: {
          ...errors,
          price: isPriceErrorExist,
          paymentAccount: isPaymentAccountErrorExist
        }
      });
    }

    if (currentStep !== CLUB_WIZARD_STEP.PAYMENT_SETTINGS || !isErrorsExist) {
      const lastClubPage = propz.get(history, ['location', 'state', 'lastClubPage'], FIRST_PAGE);
      const search = propz.get(history, ['location', 'state', 'search'], '');

      const clubId = this.getClubId();
      const clubMode = this.getClubMode();
      const dataToPost = this.getDataToPost();
      const isEditClubMode = clubMode === MODE_TYPE.EDIT;
      const isCopyClubWithParticipantsMode = clubMode === MODE_TYPE.CLUB_COPY_WITH_PARTICIPANTS;

      if (typeof clubId !== 'undefined' && isEditClubMode) {
        if (this.state.clubStatus === 'ACTIVE') {
          this.setState({ isLoading: true });
          previewActiveClubUpdate(user, clubId, dataToPost)
            .then(activeUpdatePlan => {
              this.setState({
                isLoading: false,
                activeUpdatePlan,
                pendingActiveUpdate: dataToPost,
                includePastOccurrences: false,
                selectedActiveUpdateOccurrences: [
                  ...activeUpdatePlan.eventsToUpdate.map(item => item.newStartTime),
                  ...activeUpdatePlan.occurrencesToCreate.map(item => item.startTime)
                ]
              });
            })
            .catch(err => {
              console.error(err);
              this.setState({ isLoading: false });
            });
          return;
        }
        updateClub(user, dataToPost, clubId)
          .then(club => {
            this.setState({
              isLoading: false
            });

            history.push({
              pathname: '/clubs',
              search,
              state: { clubId: club.id }
            });
          })
          .catch(err => {
            console.error(err);
            this.setState({
              isLoading: false
            });
          });
      } else {
        let clubIdCreated;
        createClub(user, dataToPost)
          .then(club => {
            clubIdCreated = club.id;

            if (isCopyClubWithParticipantsMode) {
              const participantsFiltered = this.getParticipantsFiltered(dataToPost);

              const data = {
                participants: participantsFiltered.map(student => {
                  return {
                    userId: student.userId,
                    permissionId: student.permissionId
                  };
                })
              };
              return addClubStudents(user, clubIdCreated, data);
            } else {
              return BPromise.resolve([]);
            }
          })
          .then(res => {
            this.setState({
              isLoading: false
            });

            history.push({
              pathname: '/clubs',
              search: [`page=${lastClubPage}`, 'order=createdAt:ASC', DEFAULT_CLUB_TABLE_FILTERS].join('&'),
              state: { clubId: clubIdCreated }
            });
          })
          .catch(err => {
            console.error(err);
            this.setState({
              isLoading: false
            });
          });
      }
    }
  };

  onCloseActiveUpdateModal = (): void => {
    this.setState({ activeUpdatePlan: undefined, pendingActiveUpdate: undefined, includePastOccurrences: false, selectedActiveUpdateOccurrences: [] });
  };

  onIncludePastOccurrencesChange = (): void => {
    const { user } = this.props;
    const clubId = this.getClubId();
    const pendingActiveUpdate = this.state.pendingActiveUpdate;
    const includePastOccurrences = !this.state.includePastOccurrences;
    if (!clubId || !pendingActiveUpdate) return;
    this.setState({ isLoading: true });
    previewActiveClubUpdate(user, clubId, pendingActiveUpdate, includePastOccurrences)
      .then(activeUpdatePlan => {
        const previous = new Set(this.state.selectedActiveUpdateOccurrences);
        const upcoming = [
          ...activeUpdatePlan.eventsToUpdate.map(item => item.newStartTime),
          ...activeUpdatePlan.occurrencesToCreate.map(item => item.startTime)
        ];
        const earlier = activeUpdatePlan.pastOccurrencesToCreate.map(item => item.startTime);
        const selectedActiveUpdateOccurrences = includePastOccurrences
          ? [...upcoming.filter(item => previous.has(item)), ...earlier]
          : upcoming.filter(item => previous.has(item));
        this.setState({ activeUpdatePlan, includePastOccurrences, selectedActiveUpdateOccurrences, isLoading: false });
      })
      .catch(err => {
        console.error(err);
        this.setState({ isLoading: false });
      });
  };

  onConfirmActiveUpdate = (): void => {
    const { user, history } = this.props;
    const { pendingActiveUpdate, includePastOccurrences, activeUpdatePlan, selectedActiveUpdateOccurrences } = this.state;
    const clubId = this.getClubId();
    if (!clubId || !pendingActiveUpdate || !activeUpdatePlan || !activeUpdatePlan.canApply) return;
    const search = propz.get(history, ['location', 'state', 'search'], '');
    this.setState({ isLoading: true });
    const availableOccurrences = [
      ...activeUpdatePlan.eventsToUpdate.map(item => item.newStartTime),
      ...activeUpdatePlan.occurrencesToCreate.map(item => item.startTime),
      ...(includePastOccurrences ? activeUpdatePlan.pastOccurrencesToCreate.map(item => item.startTime) : [])
    ];
    const excludedOccurrenceStartTimes = availableOccurrences.filter(item => !selectedActiveUpdateOccurrences.includes(item));
    applyActiveClubUpdate(user, clubId, pendingActiveUpdate, includePastOccurrences, activeUpdatePlan.fingerprint, excludedOccurrenceStartTimes)
      .then(result => {
        const updatedClub = result.club;
        history.push({ pathname: '/clubs', search, state: { clubId: updatedClub.id } });
      })
      .catch(err => {
        console.error(err);
        this.setState({ isLoading: false });
      });
  };

  onCancelCreateVenue = (): void => {
    this.setState({
      isCreateVenueFormOpen: false
    });
  };

  onSubmitVenueForm = (data): void => {
    const { user } = this.props;

    this.setState({
      isLoading: true
    });

    createVenue(user, data).then(venue => {
      this.setState({
        venue,
        isDirty: true,
        isLoading: false,
        isCreateVenueFormOpen: false
      });
    });
  };

  onOkSaveOnlyPassingStudentsModalClick = (): void => {
    this.setState({
      isSaveOnlyPassingStudentsModalOpen: false,
      currentStep: CLUB_WIZARD_STEP.SCHEDULE,
      maxReachedStepIndex: Math.max(this.state.maxReachedStepIndex, 1)
    });
  };

  onCancelSaveOnlyPassingStudentsModalClick = (): void => {
    this.setState({
      isSaveOnlyPassingStudentsModalOpen: false
    });
  };

  renderCreateVenueForm(): React.ReactNode {
    const { user } = this.props;
    return (
      <SimpleModal
        isOpen={this.state.isCreateVenueFormOpen}
        title="Create venue"
        onCloseClick={this.onCancelCreateVenue}
        showFooter={false}
        isBodyBold={false}
        customClass="clubWizardModal clubWizardVenueModal"
        bodyClassName="clubWizardVenueModal__body"
        dialogStyle={{ maxWidth: 720 }}
      >
        <VenueForm user={user} onCancel={this.onCancelCreateVenue} onSubmit={this.onSubmitVenueForm} />
      </SimpleModal>
    );
  }

  renderSaveOnlyPassingStudentsModal(): React.ReactNode {
    const { isSaveOnlyPassingStudentsModalOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isSaveOnlyPassingStudentsModalOpen}
        title="Some students will not be copied"
        onCloseClick={this.onCancelSaveOnlyPassingStudentsModalClick}
        showFooter={false}
        isBodyBold={false}
        customClass="clubWizardModal clubWizardPassingStudentsModal"
        bodyClassName="clubWizardPassingStudentsModal__body"
        dialogStyle={{ maxWidth: 500 }}
      >
        <div className="clubWizardPassingStudentsModal__content">
          <div className="clubWizardPassingStudentsModal__notice">
            <span aria-hidden="true">!</span>
            <div>
              <strong>The audience criteria have changed</strong>
              <p>
                Only students who match the new age and gender selection will be copied into this club. Other students
                will be left out.
              </p>
            </div>
          </div>
          <div className="clubWizardPassingStudentsModal__actions">
            <button type="button" onClick={this.onCancelSaveOnlyPassingStudentsModalClick}>
              Cancel
            </button>
            <button type="button" className="isPrimary" onClick={this.onOkSaveOnlyPassingStudentsModalClick}>
              Continue
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderActiveUpdateModal(): React.ReactNode {
    const { activeUpdatePlan: plan, includePastOccurrences, selectedActiveUpdateOccurrences } = this.state;
    if (!plan) return null;
    const formatDate = (value: string) =>
      new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
    const immutableLabels = {
      sportId: 'Activity / sport',
      gender: 'Gender',
      invitations: 'Invitation audience',
      maxParticipants: 'Maximum participants'
    };
    const blockingReasons: string[] = [];
    if (plan.blockedPastEvents.length > 0) {
      const earliestProtectedSession = plan.blockedPastEvents
        .map(item => item.startTime)
        .sort((left, right) => new Date(left).getTime() - new Date(right).getTime())[0];
      blockingReasons.push(
        `The new start date is later than an existing session. Set the start date to ${formatDate(earliestProtectedSession)} or earlier.`
      );
    }
    if (plan.immutableChanges.length > 0) {
      blockingReasons.push(
        `These fields are locked after activation: ${plan.immutableChanges.map(field => immutableLabels[field] || field).join(', ')}.`
      );
    }
    if (plan.ageConflicts.length > 0) {
      blockingReasons.push(
        `Remove ${plan.ageConflicts.length} pupil${plan.ageConflicts.length === 1 ? '' : 's'} who no longer match the selected age groups.`
      );
    }

    const selectableOccurrences = [
      ...plan.eventsToUpdate.map(item => ({ startTime: item.newStartTime, kind: 'EXISTING' })),
      ...plan.occurrencesToCreate.map(item => ({ ...item, kind: 'NEW' })),
      ...(includePastOccurrences ? plan.pastOccurrencesToCreate.map(item => ({ ...item, kind: 'PAST' })) : [])
    ].sort((left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime());
    const selectedUpcomingCount = plan.occurrencesToCreate.filter(item => selectedActiveUpdateOccurrences.includes(item.startTime)).length;
    const selectedExistingCount = plan.eventsToUpdate.filter(item => selectedActiveUpdateOccurrences.includes(item.newStartTime)).length;
    const excludedExistingCount = plan.eventsToUpdate.length - selectedExistingCount;

    return (
      <SimpleModal
        isOpen={true}
        title="Review changes to this active club"
        showFooter={false}
        isBodyBold={false}
        customClass="clubWizardModal clubWizardImpactModal"
        dialogStyle={{ maxWidth: 720 }}
        onCloseClick={this.onCloseActiveUpdateModal}
        onCloseIconClick={this.onCloseActiveUpdateModal}
        onBackdropClick={this.onCloseActiveUpdateModal}
      >
        <div className="clubWizardImpactModal__content">
          <div className={`clubWizardImpactModal__notice ${plan.canApply ? '' : 'isBlocked'}`}>
            <strong>{plan.canApply ? 'Past sessions will stay unchanged' : 'These changes cannot be applied yet'}</strong>
            <p>
              {plan.canApply
                ? `${plan.counts.pastProtected} past or started session(s) are protected. Only the upcoming series will be reconciled.`
                : 'Review the conflicts below, update the club details and try again.'}
            </p>
          </div>

          {plan.immutableChanges.length > 0 && (
            <section>
              <h3>Locked after activation</h3>
              <p>{plan.immutableChanges.map(field => immutableLabels[field] || field).join(', ')}</p>
            </section>
          )}
          {plan.blockedPastEvents.length > 0 && (
            <section className="clubWizardImpactModal__blockingSection">
              <h3>Start date cannot be moved past an existing session</h3>
              <p>
                Active club updates never remove or detach sessions that have already started. Move the club start
                date back to the earliest session below, or earlier, then review the changes again.
              </p>
              <ul>{plan.blockedPastEvents.map(item => <li key={item.eventId}>{formatDate(item.startTime)}</li>)}</ul>
            </section>
          )}
          {plan.ageConflicts.length > 0 && (
            <section>
              <h3>Remove these pupils before changing the age groups</h3>
              <ul>{plan.ageConflicts.map(item => <li key={item.permissionId}>{item.name} · {item.form}</li>)}</ul>
            </section>
          )}

          <div className="clubWizardImpactModal__stats">
            <div><strong>{selectedExistingCount}</strong><span>Upcoming updated</span></div>
            <div><strong>{selectedUpcomingCount}</strong><span>Upcoming created</span></div>
            <div><strong>{plan.counts.futureRemoved + excludedExistingCount}</strong><span>Upcoming removed</span></div>
            <div><strong>{plan.counts.pastProtected}</strong><span>Past protected</span></div>
          </div>
          {plan.changedFields.schedule && plan.newScheduleStart && (
            <section>
              <h3>Schedule anchor</h3>
              <p>
                The repeat pattern will be calculated from <strong>{formatDate(plan.newScheduleStart)}</strong>. This
                determines which week is the first week when using “repeat every N weeks”.
              </p>
            </section>
          )}

          {selectableOccurrences.length > 0 && (
            <section className="clubWizardImpactModal__occurrences">
              <div className="clubWizardImpactModal__occurrencesHeader">
                <div><h3>Resulting session dates</h3><p>Review the proposed schedule and untick any future sessions you do not want to keep or create.</p></div>
                <span>{selectedActiveUpdateOccurrences.filter(item => selectableOccurrences.some(occurrence => occurrence.startTime === item)).length} of {selectableOccurrences.length} selected</span>
              </div>
              <div className="clubWizardImpactModal__occurrenceActions">
                <button type="button" onClick={() => this.setState({ selectedActiveUpdateOccurrences: selectableOccurrences.map(item => item.startTime) })}>Select all</button>
                <button type="button" onClick={() => this.setState({ selectedActiveUpdateOccurrences: [] })}>Clear</button>
              </div>
              <div className="clubWizardImpactModal__occurrenceList">
                {selectableOccurrences.map(item => {
                  const checked = selectedActiveUpdateOccurrences.includes(item.startTime);
                  const occurrenceClassName = [
                    checked ? 'isSelected' : '',
                    item.kind === 'NEW' ? 'isNew' : '',
                    item.kind === 'PAST' ? 'isPast' : ''
                  ].filter(Boolean).join(' ');
                  return <label key={item.startTime} className={occurrenceClassName}>
                    <input type="checkbox" checked={checked} onChange={event => this.setState({ selectedActiveUpdateOccurrences: event.target.checked ? [...selectedActiveUpdateOccurrences, item.startTime] : selectedActiveUpdateOccurrences.filter(value => value !== item.startTime) })} />
                    <span><strong>{formatDate(item.startTime)}</strong><small>{item.kind === 'PAST' ? 'Earlier missing session' : item.kind === 'EXISTING' ? 'Existing upcoming session' : 'New upcoming session'}</small></span>
                  </label>;
                })}
              </div>
            </section>
          )}
          {plan.eventsToRemove.length > 0 && (
            <section><h3>Upcoming sessions removed from the schedule</h3><ul>{plan.eventsToRemove.map(item => <li key={item.eventId}>{formatDate(item.startTime)}</li>)}</ul></section>
          )}
          {plan.pastOccurrencesToCreate.length > 0 && (
            <label className="clubWizardImpactModal__backfill">
              <input type="checkbox" checked={includePastOccurrences} onChange={this.onIncludePastOccurrencesChange} />
              <span>
                <strong>Add {plan.pastOccurrencesToCreate.length} missing earlier session(s)</strong>
                <small>They will use the current participant list and will not contain attendance or results.</small>
              </span>
            </label>
          )}

          <div className={`clubWizardImpactModal__actions ${plan.canApply ? '' : 'isBlocked'}`}>
            {!plan.canApply && (
              <div className="clubWizardImpactModal__blockedExplanation" role="alert">
                <strong>Why “Apply changes” is unavailable</strong>
                <ul>
                  {blockingReasons.map(reason => <li key={reason}>{reason}</li>)}
                </ul>
              </div>
            )}
            <div className="clubWizardImpactModal__actionButtons">
              <button type="button" onClick={this.onCloseActiveUpdateModal}>Keep editing</button>
              <button type="button" className="isPrimary" disabled={!plan.canApply} onClick={this.onConfirmActiveUpdate}>
                Apply changes
              </button>
            </div>
          </div>
        </div>
      </SimpleModal>
    );
  }

  onRailStepClick = (step: Step, index: number) => {
    const isEditMode = this.getClubMode() === MODE_TYPE.EDIT;
    if (isEditMode || index <= this.state.maxReachedStepIndex) {
      this.setState({ currentStep: step });
    }
  };

  renderDsStepRail(): React.ReactNode {
    const { currentStep } = this.state;
    const steps = this.getSteps();
    const currentStepIndex = steps.findIndex(step => step === currentStep);
    const isEditMode = this.getClubMode() === MODE_TYPE.EDIT;
    const maxReachedStepIndex = isEditMode ? steps.length - 1 : this.state.maxReachedStepIndex;

    return (
      <aside className="clubWizardDs__rail">
        <div className="clubWizardDs__steps">
          {steps.map((step, index) => {
            const isActive = step === currentStep;
            const isDone = index !== currentStepIndex && index <= maxReachedStepIndex;
            return (
              <button
                key={step}
                type="button"
                className={`clubWizardDs__step ${isActive ? 'isActive' : ''} ${isDone ? 'isDone' : ''}`}
                disabled={!isEditMode && index > maxReachedStepIndex}
                onClick={() => this.onRailStepClick(step, index)}
              >
                <span className="clubWizardDs__stepDot">{isDone ? '✓' : index + 1}</span>
                <span className="clubWizardDs__stepCopy">
                  <strong>{CLUB_WIZARD_STEP_MAPPING[step]}</strong>
                  <small>{CLUB_WIZARD_STEP_HINTS[step]}</small>
                </span>
              </button>
            );
          })}
        </div>
        <p>{CLUB_WIZARD_STEP_EXPLANATIONS[currentStep]}</p>
      </aside>
    );
  }

  renderDsContext(): React.ReactNode {
    const { name, sport, genderItems, ageItems, start, clubStatus, isDirty, scheduleMode, specificDates } = this.state;
    const activity = propz.get(sport, ['name'], 'Activity not set');
    const genders = genderItems.filter(item => item.isChecked).map(item => item.name);
    const ageIndexes = ageItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.isChecked)
      .map(({ item, index }) => (typeof item.age === 'number' ? item.age : index));
    const ages =
      ageIndexes.length === 0 || ageIndexes.length === ageItems.length
        ? 'All ages'
        : getAgesGroupFormatted({ ages: [...ageIndexes] }, { user: this.props.user }).replace(/\s+-\s+/g, '-');
    const audience = `${genders.length ? genders.join(' & ') : 'Audience not set'} · ${ages || 'All ages'}`;
    const seasonStart = scheduleMode === 'DATES_LIST' && specificDates.length ? specificDates[0] : start;
    const season = getAcademicYearString(seasonStart).replace('/', ' / ');

    return (
      <div className="clubWizardDsContextBar">
        <span className="clubWizardDsContextBar__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
          </svg>
        </span>
        <span className="clubWizardDsContextBar__copy">
          <strong>{name || 'New club'}</strong>
          <small>
            {activity} · {audience} · {season}
          </small>
        </span>
        <span className="clubWizardDsContextBar__spacer" />
        <span className="clubWizardDsContextBar__status">{String(clubStatus).toLowerCase()}</span>
        {isDirty && <span className="clubWizardDsContextBar__dirty">● Unsaved changes</span>}
      </div>
    );
  }

  render() {
    const {
      currentStep,
      isLoading,
      isCancelModalOpen,
      isCreateVenueFormOpen,
      isSaveOnlyPassingStudentsModalOpen,
      activeUpdatePlan
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const steps = this.getSteps();
    const currentStepIndex = steps.findIndex(step => step === currentStep);
    const totalSteps = steps.length;

    const isNext = currentStepIndex < totalSteps - 1;
    const isSave = currentStepIndex === totalSteps - 1;
    const isPrevious = currentStepIndex > 0;

    const classes =
      isCancelModalOpen || isCreateVenueFormOpen || isSaveOnlyPassingStudentsModalOpen || activeUpdatePlan ? 'modal-open' : '';

    return (
      <div className={`${classes} clubWizardDs`}>
        {this.renderCancelModal()}
        {this.renderCreateVenueForm()}
        {this.renderSaveOnlyPassingStudentsModal()}
        {this.renderActiveUpdateModal()}
        {this.renderDsContext()}
        <div className="clubWizardDs__card">
          {this.renderDsStepRail()}
          <main className="clubWizardDs__content">
            <div className="clubWizardDs__body">{this.renderViewByStep()}</div>
            <footer className="clubWizardDs__footer">
              <button type="button" className="clubWizardDs__btn clubWizardDs__btnGhost" onClick={this.onCancelClick}>
                Cancel
              </button>
              <span className="clubWizardDs__spacer" />
              <button type="button" className="clubWizardDs__btn clubWizardDs__btnGhost" onClick={this.onSaveClick}>
                Save &amp; exit
              </button>
              {isPrevious && (
                <button
                  type="button"
                  className="clubWizardDs__btn clubWizardDs__btnSecondary"
                  onClick={this.onPreviousClick}
                >
                  Back
                </button>
              )}
              {isNext && <Button text="Next" customClass="sit-btn sit-btn--primary" onClick={this.onNextClick} />}
              {isSave && (
                <Button text="Save changes" customClass="sit-btn sit-btn--primary" onClick={this.onSaveClick} />
              )}
            </footer>
          </main>
        </div>
      </div>
    );
  }
}
